// streamSettings.js
//
// Streaming-settings state, fed from two directions:
//  1. The /streaming-settings fetch (all appearance settings + the settings_epoch).
//  2. In-band ID3 cues from the live video stream (see ../../streaming/id3Cues.js), which carry
//     the authoritative giving-banner visibility and the current epoch.
//
// While ID3 state is flowing, the fixed settings poll stops: the client refetches only when the
// in-band epoch is newer than the last fetched payload (fetch-on-change), plus a slow safety poll.
// Before/after the live window — when no stream exists to carry data — the regular poll cadence
// still applies, with jitter and exponential backoff on failure so a MonkCMS brownout doesn't get
// hammered by synchronized retries from every viewer.

const BASE_POLL_MS = 10000; // Pre/post-live cadence (matches the previous fixed poll).
const SAFETY_POLL_MS = 120000; // While in-band state is flowing.
const MAX_BACKOFF_MS = 120000;
const EPOCH_REFETCH_MAX_ATTEMPTS = 4;

/** Spread timers across viewers so they never stampede in lockstep. */
function jitter(ms) {
  return Math.round(ms * (0.85 + Math.random() * 0.3))
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const state = {
  streamSettings: {}, // Holds the streaming settings as fetched
  settingsEpoch: 0, // settings_epoch from the last successful fetch
  inbandActive: false, // True while ID3 state cues are flowing from a live player
  inbandState: null, // Last in-band state payload ({epoch, banner_visibility, ...})
  pollingTimeoutId: null, // Recursive setTimeout id
  consecutiveFailures: 0,
  epochRefetchTarget: 0, // Highest in-band epoch we are (or were) converging to
}

const mutations = {
  SET_STREAM_SETTINGS(state, payload) {
    state.streamSettings = payload
    state.settingsEpoch = payload && payload.settings_epoch ? parseInt(payload.settings_epoch, 10) || 0 : 0
  },
  SET_POLLING_TIMEOUT_ID(state, timeoutId) {
    state.pollingTimeoutId = timeoutId
  },
  SET_INBAND_ACTIVE(state, active) {
    state.inbandActive = active
  },
  SET_INBAND_STATE(state, payload) {
    state.inbandState = payload
  },
  SET_CONSECUTIVE_FAILURES(state, count) {
    state.consecutiveFailures = count
  },
  SET_EPOCH_REFETCH_TARGET(state, epoch) {
    state.epochRefetchTarget = epoch
  },
}

const actions = {
  async fetchStreamSettings({ commit, state }) {
    if (!window.base_domain || !window.Church || !window.Church.slug) {
      return
    }

    try {
      const response = await fetch(`//api.${window.base_domain}/${window.Church.slug}/streaming-settings`)
      if (response.status === 200) {
        const finalRes = await response.json()
        commit('SET_STREAM_SETTINGS', finalRes.data)
        commit('SET_CONSECUTIVE_FAILURES', 0)
      } else {
        // Keep the last-known settings on failure; the in-band state tag stays authoritative.
        commit('SET_CONSECUTIVE_FAILURES', state.consecutiveFailures + 1)
        console.error('Failed to fetch stream settings, status: ' + response.status)
      }
    } catch (error) {
      commit('SET_CONSECUTIVE_FAILURES', state.consecutiveFailures + 1)
      // Network errors (CORS, DNS, connectivity) are expected in some embed contexts
      console.error('Error fetching stream settings:', error.message)
    }
  },

  /**
   * Recursive scheduling instead of setInterval so the cadence can change between ticks:
   * slow safety poll while in-band state flows, regular cadence otherwise, doubled (capped)
   * after consecutive failures.
   */
  scheduleNextPoll({ commit, dispatch, state }) {
    if (state.pollingTimeoutId) {
      clearTimeout(state.pollingTimeoutId)
    }

    let interval = state.inbandActive ? SAFETY_POLL_MS : BASE_POLL_MS

    if (state.consecutiveFailures > 0) {
      interval = Math.min(interval * Math.pow(2, state.consecutiveFailures), MAX_BACKOFF_MS)
    }

    const timeoutId = setTimeout(async () => {
      await dispatch('fetchStreamSettings')
      dispatch('scheduleNextPoll')
    }, jitter(interval))

    commit('SET_POLLING_TIMEOUT_ID', timeoutId)
  },

  startPolling({ dispatch }) {
    dispatch('fetchStreamSettings')
    dispatch('scheduleNextPoll')
  },

  stopPolling({ state, commit }) {
    if (state.pollingTimeoutId) {
      clearTimeout(state.pollingTimeoutId)
      commit('SET_POLLING_TIMEOUT_ID', null)
    }
  },

  /**
   * Called by the player when ID3 state cues start or stop flowing. Active in-band state drops
   * the settings poll to the slow safety cadence.
   */
  setInbandActive({ commit, dispatch, state }, active) {
    if (state.inbandActive === active) {
      return
    }

    commit('SET_INBAND_ACTIVE', active)

    if (!active) {
      commit('SET_INBAND_STATE', null)
    }

    // Re-plan the next tick at the new cadence (only if polling was running).
    if (state.pollingTimeoutId) {
      dispatch('scheduleNextPoll')
    }
  },

  /**
   * An in-band state tag arrived. Apply it immediately (the getter overlays it on the fetched
   * settings) and, when its epoch is newer than the payload we last fetched, refetch settings.
   */
  applyInbandState({ commit, dispatch, state }, payload) {
    const epoch = parseInt(payload.epoch, 10) || 0

    // The repeating tag is authoritative: never regress to an older epoch's state.
    if (state.inbandState && epoch < (parseInt(state.inbandState.epoch, 10) || 0)) {
      return
    }

    commit('SET_INBAND_STATE', payload)

    if (epoch > state.settingsEpoch && epoch > state.epochRefetchTarget) {
      commit('SET_EPOCH_REFETCH_TARGET', epoch)
      dispatch('refetchForEpoch', epoch)
    }
  },

  /**
   * A one-shot in-band event arrived (already deduped by event_id in the cue reader).
   * Banner events fold into the visibility overlay; other types have no UI here yet.
   */
  applyInbandEvent({ commit, state }, payload) {
    if (payload.type === 'giving_banner_show' || payload.type === 'giving_banner_hide') {
      const current = state.inbandState || {}
      commit('SET_INBAND_STATE', {
        ...current,
        banner_visibility: {
          type: 'control_while_live',
          expanded_mode: payload.type === 'giving_banner_show',
        },
      })
    }
  },

  /**
   * Fetch-on-change with convergence: the server response is briefly cached, so retry (with
   * jitter and spacing) until the fetched settings_epoch catches up with the in-band epoch.
   */
  async refetchForEpoch({ dispatch, state }, targetEpoch) {
    for (let attempt = 0; attempt < EPOCH_REFETCH_MAX_ATTEMPTS; attempt++) {
      // First attempt is spread over 0-5s to absorb every viewer refetching at once.
      await delay(attempt === 0 ? Math.random() * 5000 : jitter(10000 * attempt))

      if (state.settingsEpoch >= targetEpoch || state.epochRefetchTarget > targetEpoch) {
        return
      }

      await dispatch('fetchStreamSettings')

      if (state.settingsEpoch >= targetEpoch) {
        return
      }
    }
  },
}

const getters = {
  /**
   * The settings the UI consumes: the fetched payload with the in-band state overlaid.
   * While live, the ID3 state tag wins for banner visibility — it arrives at the matching
   * video moment and works even when the settings fetch is failing.
   */
  streamSettings(state) {
    if (!state.inbandActive || !state.inbandState || !state.inbandState.banner_visibility) {
      return state.streamSettings
    }

    return {
      ...state.streamSettings,
      give_banner: {
        ...(state.streamSettings.give_banner || {}),
        banner_visibility: state.inbandState.banner_visibility,
      },
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
